import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Menu,
  Checkbox,
  ListSeparator,
  Input,
  Button,
  Caption1,
  TrashIcon,
  IconButton,
  Caption2,
  Label,
  Tooltip,
  InfoSmallIcon,
} from "@activecollab/components";
import classNames from "classnames";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";

export const TemplateTaskPanelDueOnForm = ({ trigger, startOn, dueOn, onChange, isFromList, targetClassName }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const setIsMenuOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const renderTrigger = useCallback(() => {
    if (React.isValidElement(trigger)) {
      return React.cloneElement(trigger, { onClick: setIsMenuOpen, open, className: targetClassName });
    }

    return (
      <div
        className={targetClassName}
        onClick={setIsMenuOpen}
      >
        {trigger}
      </div>
    );
  }, [trigger, targetClassName, setIsMenuOpen, open]);

  const formik = useFormik({
    initialValues: {
      startIn: startOn,
      dueIn: dueOn,
      showStartDate: startOn && dueOn ? startOn !== dueOn : false,
    },
    enableReinitialize: true,
    onSubmit: ({ startIn, dueIn, showStartDate }) => {
      let errors = {};
      const errorText = t("Value must be between 1 and 365");

      if (showStartDate && (startIn > 365 || startIn < 0)) {
        formik.setFieldError("startIn", errorText);
        errors.startIn = errorText;
      }

      if (dueIn > 365 || dueIn < 0) {
        formik.setFieldError("dueIn", errorText);
        errors.dueIn = errorText;
      }

      if (Object.values(errors).length) {
        return focusFirstInvalidField(errors, true, ["startIn", "dueIn"]);
      }

      if (onChange) {
        const dateRange = isFromList
          ? { start_on: showStartDate ? startIn : dueIn, due_on: dueIn }
          : { start_in: showStartDate ? startIn : dueIn, due_in: dueIn };
        onChange(dateRange);
      }
      formik.setFieldValue("showStartDate", showStartDate && startIn > 0 && startIn !== dueIn);
      setOpen(false);
    },
    validate: (values) => {
      const errors = {};

      if (values.showStartDate && values.startIn > values.dueIn) {
        errors.startIn = t("Value must be lower then due on value");
      }

      return errors;
    },
  });

  const onTrashIconClick = useCallback(() => {
    formik.setValues({ startIn: "", dueIn: "", showStartDate: false });
    formik.handleSubmit();
    setOpen(false);
  }, [formik]);

  const onDueInBlur = useCallback(() => {
    if (formik.values.startIn > formik.values.dueIn) {
      formik.setFieldValue("startIn", formik.values.dueIn);
    }
  }, [formik]);

  const onStartInBlur = useCallback(() => {
    if (formik.values.startIn > formik.values.dueIn) {
      formik.setFieldValue("dueIn", formik.values.startIn);
    }
  }, [formik]);

  const handleCancel = useCallback(() => {
    formik.setValues({
      startIn: startOn,
      dueIn: dueOn,
      showStartDate: startOn !== dueOn,
    });
    setOpen(false);
  }, [dueOn, formik, startOn]);

  return (
    <Menu
      open={open}
      target={renderTrigger()}
      className="tw-px-4 tw-py-3 tw-w-72"
      onClose={handleCancel}
    >
      <div className="tw-flex tw-gap-2 tw-items-center">
        <Checkbox
          id="show-start-date"
          name="showStartDate"
          data-testid="show-start-date"
          onChange={formik.handleChange}
          checked={formik.values.showStartDate}
        />
        <Label
          htmlFor="show-start-date"
          className="tw-self-center tw-cursor-pointer tw-select-none ds-color-900"
          weight="regular"
          size="small"
        >
          {t("This task has a start date")}
        </Label>
      </div>
      <ListSeparator className="tw-mx-0" />
      {formik.values.showStartDate ? (
        <Caption1 className="tw-flex tw-gap-1 tw-items-center tw-mb-2">
          {t("Start on")}{" "}
          <Input
            ref={(el) => (fieldRef.current.startIn = el)}
            name="startIn"
            data-testid="start-date"
            size="small"
            type="number"
            placeholder={0}
            max={365}
            invalid={formik.errors?.startIn}
            value={formik.values.startIn > 0 ? formik.values.startIn : ""}
            onChange={formik.handleChange}
            onBlur={onStartInBlur}
            className={classNames(
              "input-without-arrows",
              { "tw-w-11": !formik?.errors.startIn },
              { "tw-w-16": formik?.errors.startIn }
            )}
            endAdornment={
              formik?.errors.startIn ? (
                <Tooltip title={formik.errors.startIn}>
                  <InfoSmallIcon
                    fill="var(--red-alert)"
                    className="tw-cursor-help tw-mr-1"
                  />
                </Tooltip>
              ) : null
            }
          />{" "}
          {t("day of project")}
        </Caption1>
      ) : null}
      <Caption1 className="tw-flex tw-gap-1 tw-items-center">
        {t("Due on")}{" "}
        <Input
          ref={(el) => (fieldRef.current.dueIn = el)}
          name="dueIn"
          data-testid="due-date"
          size="small"
          type="number"
          placeholder={0}
          max={365}
          onBlur={onDueInBlur}
          invalid={formik.errors?.dueIn}
          value={formik.values.dueIn > 0 ? formik.values.dueIn : ""}
          onChange={formik.handleChange}
          className={classNames(
            "input-without-arrows",
            { "tw-w-11": !formik?.errors.dueIn },
            { "tw-w-16": formik?.errors.dueIn }
          )}
          endAdornment={
            formik?.errors.dueIn ? (
              <Tooltip title={formik.errors.dueIn}>
                <InfoSmallIcon
                  fill="var(--red-alert)"
                  className="tw-cursor-help tw-mr-1"
                />
              </Tooltip>
            ) : null
          }
        />{" "}
        {t("day of project")}
      </Caption1>
      <Caption2
        color="tertiary"
        className="tw-mt-2"
      >
        {t("Scheduling ignores non-working days.")}
      </Caption2>
      <ListSeparator className="tw-mx-0" />
      <div className="tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex">
          <Button
            size="small"
            type="button"
            data-testid="set-button"
            onClick={formik.handleSubmit}
            className="tw-mr-3"
          >
            {t("Set")}
          </Button>
          <Button
            size="small"
            type="button"
            onClick={handleCancel}
            variant="secondary"
          >
            {t("Cancel")}
          </Button>
        </div>
        <IconButton
          type="button"
          variant="text gray"
          onClick={onTrashIconClick}
        >
          <TrashIcon />
        </IconButton>
      </div>
    </Menu>
  );
};

TemplateTaskPanelDueOnForm.propTypes = {
  trigger: PropTypes.node,
  startOn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dueOn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  isFromList: PropTypes.bool,
  targetClassName: PropTypes.string,
};
